
import React, { useEffect, useState } from "react";
import { Card, Spinner, ButtonGroup, Button, Row, Col, Image } from "react-bootstrap";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { fetchIndiaChart } from "../services/marketService";

const FH = process.env.REACT_APP_FINNHUB_KEY;
const TD = process.env.REACT_APP_TWELVE_KEY;

const RANGES = {
  "1D": { interval: "5min", size: 78 },
  "1M": { interval: "1day", size: 22 },
  "6M": { interval: "1day", size: 130 },
  "1Y": { interval: "1day", size: 252 },
};

const chartCache = new Map();
const inflight = new Map();
const infoCache = new Map();

const fmtTime = (ms, range) => {
  const d = new Date(ms);
  return range === "1D"
    ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Kolkata" })
    : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// US chart (Twelve Data)
const loadUSChart = (symbol, range) => {
  const key = `US|${symbol}|${range}`;
  if (chartCache.has(key)) return Promise.resolve(chartCache.get(key));
  if (inflight.has(key)) return inflight.get(key);
  const { interval, size } = RANGES[range];
  const p = fetch(
    `https://api.twelvedata.com/time_series?symbol=${symbol}&interval=${interval}&outputsize=${size}&apikey=${TD}`
  )
    .then((r) => r.json())
    .then((d) => {
      if (d.status === "error" || !d.values) throw new Error(d.message || "Chart data nahi mila");
      const rows = d.values
        .map((v) => ({
          time: range === "1D" ? v.datetime.slice(11, 16) : v.datetime.slice(0, 10),
          price: parseFloat(v.close),
          volume: parseFloat(v.volume),
        }))
        .reverse();
      chartCache.set(key, rows);
      return rows;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
};

// India chart (backend -> Yahoo)
const loadINChart = async (symbol, range) => {
  const key = `IN|${symbol}|${range}`;
  const hit = chartCache.get(key);
  if (hit && Date.now() - hit.t < 60000) return hit.rows; // 1 min cache
  const candles = await fetchIndiaChart(symbol, range);
  const rows = candles.map((c) => ({ time: fmtTime(c.time, range), price: c.price, volume: c.volume }));
  chartCache.set(key, { t: Date.now(), rows });
  return rows;
};

const loadUSInfo = async (symbol) => {
  if (infoCache.has(symbol)) return infoCache.get(symbol);
  const [p, m] = await Promise.all([
    fetch(`https://finnhub.io/api/v1/stock/profile2?symbol=${symbol}&token=${FH}`).then((r) => r.json()),
    fetch(`https://finnhub.io/api/v1/stock/metric?symbol=${symbol}&metric=all&token=${FH}`).then((r) => r.json()),
  ]);
  const info = { profile: p || {}, metric: m?.metric || {} };
  infoCache.set(symbol, info);
  return info;
};

const num = (v, d = 2) =>
  v === undefined || v === null || v === "" || isNaN(v)
    ? "—"
    : Number(v).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });

const compact = (v) =>
  v === undefined || v === null || isNaN(v)
    ? "—"
    : Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 2 }).format(v);

const Stat = ({ label, value }) => (
  <Col xs={6} md={4} className="mb-3 text-start">
    <small className="text-muted d-block">{label}</small>
    <span className="fw-semibold">{value}</span>
  </Col>
);

const StockDetail = ({ symbol, quote, market = "US", onClose }) => {
  const isIN = market === "IN";
  const isIndex = symbol.startsWith("^");
  const cur = isIN ? (isIndex ? "" : "₹") : "$";

  const [range, setRange] = useState("1M");
  const [chart, setChart] = useState([]);
  const [info, setInfo] = useState({ profile: {}, metric: {} });
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState("");

  // US company info (Finnhub)
  useEffect(() => {
    if (isIN) return;
    let cancelled = false;
    loadUSInfo(symbol).then((i) => !cancelled && setInfo(i)).catch(() => {});
    return () => { cancelled = true; };
  }, [symbol, isIN]);

  // Chart
  useEffect(() => {
    let cancelled = false;
    setChartLoading(true);
    setChartError("");
    (isIN ? loadINChart(symbol, range) : loadUSChart(symbol, range))
      .then((rows) => {
        if (cancelled) return;
        setChart(rows);
        setChartLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setChart([]);
        setChartError(
          /credits/i.test(err.message)
            ? "API limit hit ho gayi. 1 minute ruk ke range button dobara dabao."
            : err.message
        );
        setChartLoading(false);
      });
    return () => { cancelled = true; };
  }, [symbol, range, isIN]);

  const { profile, metric } = info;
  const q = quote || {};
  const qUp = q.d >= 0;
  const up = chart.length > 1 ? chart[chart.length - 1].price >= chart[0].price : true;
  const color = up ? "#198754" : "#dc3545";

  const volume = isIN
    ? q.v
    : chart.length === 0
    ? undefined
    : range === "1D"
    ? chart.reduce((s, r) => s + (r.volume || 0), 0)
    : chart[chart.length - 1].volume;

  const title = isIN ? q.name || symbol : profile.name || "";
  const subtitle = isIN
    ? [q.exchange, q.currency].filter(Boolean).join(" • ")
    : profile.finnhubIndustry || "";

  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Body>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            {!isIN && profile.logo && <Image src={profile.logo} width="36" height="36" className="me-2" />}
            <div className="text-start">
              <h5 className="mb-0 fw-bold">{isIN ? symbol.replace(".NS", "").replace(".BO", "") : symbol}</h5>
              <small className="text-muted">{title} {subtitle ? `• ${subtitle}` : ""}</small>
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={onClose}>✕</Button>
        </div>

        {/* Price */}
        {q.c ? (
          <div className="text-start mb-2">
            <h3 className="fw-bold mb-0">{cur}{num(q.c)}</h3>
            <span style={{ color: qUp ? "green" : "red" }}>
              {qUp ? "+" : ""}{num(q.d)} ({qUp ? "+" : ""}{num(q.dp)}%)
            </span>
          </div>
        ) : null}

        {/* Range buttons */}
        <ButtonGroup size="sm" className="mb-2">
          {Object.keys(RANGES).map((r) => (
            <Button key={r} variant={range === r ? "primary" : "outline-primary"} onClick={() => setRange(r)}>
              {r}
            </Button>
          ))}
        </ButtonGroup>

        {/* Chart */}
        <div style={{ width: "100%", height: 260 }}>
          {chartLoading ? (
            <div className="text-center pt-5"><Spinner animation="border" /></div>
          ) : chart.length === 0 ? (
            <p className="text-muted pt-5">{chartError || "Chart data available nahi hai"}</p>
          ) : (
            <ResponsiveContainer>
              <AreaChart data={chart}>
                <defs>
                  <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="time" tick={{ fontSize: 11 }} minTickGap={40} />
                <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11 }} width={55} />
                <Tooltip formatter={(v) => [`${cur}${num(v)}`, "Price"]} />
                <Area type="monotone" dataKey="price" stroke={color} strokeWidth={2} fill="url(#fill)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Stats */}
        <h6 className="fw-semibold text-start mt-3 mb-3">Key Stats</h6>
        <Row>
          <Stat label="Open" value={q.o ? `${cur}${num(q.o)}` : "—"} />
          <Stat label="Previous Close" value={q.pc ? `${cur}${num(q.pc)}` : "—"} />
          <Stat label="Day High" value={q.h ? `${cur}${num(q.h)}` : "—"} />
          <Stat label="Day Low" value={q.l ? `${cur}${num(q.l)}` : "—"} />
          <Stat label="Volume" value={compact(volume)} />

          {isIN ? (
            <>
              <Stat label="52W High" value={q.high52 ? `${cur}${num(q.high52)}` : "—"} />
              <Stat label="52W Low" value={q.low52 ? `${cur}${num(q.low52)}` : "—"} />
              <Stat label="Exchange" value={q.exchange || "—"} />
              <Stat label="Currency" value={q.currency || "—"} />
            </>
          ) : (
            <>
              <Stat
                label="Avg Volume (10D)"
                value={metric["10DayAverageTradingVolume"] ? compact(metric["10DayAverageTradingVolume"] * 1e6) : "—"}
              />
              <Stat label="52W High" value={metric["52WeekHigh"] ? `$${num(metric["52WeekHigh"])}` : "—"} />
              <Stat label="52W Low" value={metric["52WeekLow"] ? `$${num(metric["52WeekLow"])}` : "—"} />
              <Stat
                label="Market Cap"
                value={profile.marketCapitalization ? `$${compact(profile.marketCapitalization * 1e6)}` : "—"}
              />
              <Stat label="P/E Ratio" value={num(metric.peBasicExclExtraTTM)} />
              <Stat label="EPS (TTM)" value={num(metric.epsTTM)} />
              <Stat label="Beta" value={num(metric.beta)} />
              <Stat
                label="Dividend Yield"
                value={metric.dividendYieldIndicatedAnnual ? `${num(metric.dividendYieldIndicatedAnnual)}%` : "—"}
              />
              <Stat label="Exchange" value={profile.exchange || "—"} />
              <Stat label="Country" value={profile.country || "—"} />
            </>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default StockDetail;